import axios, { AxiosError } from 'axios';

export interface TarifType {
  type: string;
  prix: string[];
}

export interface TableauTarif {
  tableau_index: number;
  headers: string[] | null;
  rows: string[][];
}

export interface InformationGenerale {
  texte: string;
  prix_trouves: string[];
}

export interface TarifsLegalinData {
  types_annonce: TarifType[];
  tarifs_forfaitaires: TableauTarif[];
  tarifs_caractere: string[];
  informations_generales: InformationGenerale[];
}

export interface ScraperMetadata {
  source: 'cache' | 'scraping';
  cachedAt?: string;
  scrapedAt?: string;
  expiresAt?: string;
  itemsFound?: {
    types_annonce: number;
    tableaux: number;
    informations: number;
  };
}

export interface HistoryItem {
  timestamp: string;
  itemsCount: {
    types_annonce: number;
    tableaux: number;
    informations: number;
  };
  success: boolean;
}


export interface ApiResponse<T> {
  success: boolean;
  data: T;
  metadata?: ScraperMetadata;
  message?: string;
}

const API_BASE_URL = '/api_alo';

const axiosInstance = axios.create({
  baseURL: `${API_BASE_URL}/scraper`,
  headers: {
    'Content-Type': 'application/json'
  },
  timeout: 120000
});

// ✅ Intercepteur de requête pour logger
axiosInstance.interceptors.request.use(
  (config) => {
    console.log('[ScraperService] Requête:', config.method?.toUpperCase(), config.url);
    return config;
  },
  (error) => {
    console.error('[ScraperService] Erreur requête:', error);
    return Promise.reject(error);
  }
);

// ✅ Intercepteur de réponse pour logger
axiosInstance.interceptors.response.use(
  (response) => {
    console.log('[ScraperService] Réponse:', response.status, response.config.url);
    return response;
  },
  (error) => {
    console.error('[ScraperService] Erreur réponse:', error.message);
    return Promise.reject(error);
  }
);

class ScraperService {

  async getTarifsLegalin(forceRefresh = false): Promise<ApiResponse<TarifsLegalinData>> {
  try {
    console.log('[ScraperService] URL appelée:', '/api_alo/scraper/tarifs-legalin');
    console.log('[ScraperService] forceRefresh:', forceRefresh);
    
    const response = await axiosInstance.get<ApiResponse<TarifsLegalinData>>(
      '/tarifs-legalin',
      {
        params: { force: forceRefresh },
        headers: this.getAuthHeaders()
      }
    );
    
    console.log('[ScraperService] Réponse reçue:', response.status);
    return response.data;
  } catch (error) {
    console.error('[ScraperService] Erreur complète:', error);
    throw this.handleError(error);
  }
}

  async refreshTarifs(): Promise<ApiResponse<TarifsLegalinData>> {
    try {
      console.log('[ScraperService] refreshTarifs - démarrage du scraping...');
      
      const response = await axiosInstance.get<ApiResponse<TarifsLegalinData>>(
        '/tarifs-legalin/refresh',
        {
          headers: this.getAuthHeaders()
        }
      );

      console.log('[ScraperService] Scraping terminé:', response.data.metadata);
      return response.data;
    } catch (error) {
      console.error('[ScraperService] Erreur refreshTarifs:', error);
      throw this.handleError(error);
    }
  }

  async getHistory(): Promise<HistoryItem[]> {
    try {
      console.log('[ScraperService] Chargement de l\'historique...');
      
      const response = await axiosInstance.get<ApiResponse<HistoryItem[]>>(
        '/tarifs-legalin/history',
        {
          headers: this.getAuthHeaders()
        }
      );

      const history = response.data.data || [];
      console.log('[ScraperService] Historique chargé:', history.length, 'entrées');
      return history;
    } catch (error) {
      console.error('[ScraperService] Erreur getHistory:', error);
      // Retourner un tableau vide au lieu de throw pour éviter de casser l'UI
      return [];
    }
  }

  private getAuthHeaders(): Record<string, string> {
    const token = localStorage.getItem('token');
    return {
      Authorization: `Bearer ${token ?? ''}`
    };
  }

  private handleError(error: unknown): Error {
    if (axios.isAxiosError(error)) {
      const axiosError = error as AxiosError<any>;

      if (axiosError.response) {
        const message = 
          axiosError.response.data?.message ||
          axiosError.response.data?.error ||
          'Erreur serveur';
        
        console.error('[ScraperService] Erreur HTTP:', axiosError.response.status, message);
        return new Error(message);
      }

      if (axiosError.request) {
        console.error('[ScraperService] Pas de réponse du serveur');
        return new Error(
          'Impossible de joindre le serveur. Vérifiez que le backend est démarré sur le port 5200.'
        );
      }
    }

    console.error('[ScraperService] Erreur inconnue:', error);
    return new Error('Erreur inconnue');
  }
}

const scraperService = new ScraperService();
export default scraperService;